import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link, useNavigate } from "react-router-dom";
import { API_BASE } from "../lib/api";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Loader2, Plus, Trash2, ArrowRight, Search, ScanEye, RefreshCw,
} from "lucide-react";

const API = `${API_BASE}/api`;

// Cheap error normaliser — Pydantic 422 responses put an array of validation
// errors on `detail`, which crashes toasts if rendered as-is (React error #31
// when a raw object hits JSX). Coerce to a friendly string.
function errMsg(err, fallback) {
  const d = err?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d) && d[0]?.msg) return d.map((x) => x.msg).join("; ");
  return err?.message || fallback;
}

// One place to render the workflow status of a report — colours + copy live
// in the same map so future statuses only need one edit.
const STATUS_STYLE = {
  draft:            { color: "bg-zinc-100 text-zinc-700 border-zinc-200",       label: "Draft" },
  discovering:      { color: "bg-blue-50 text-blue-700 border-blue-200",         label: "Finding competitors…" },
  pending_confirm:  { color: "bg-amber-50 text-amber-700 border-amber-200",      label: "Confirm competitors" },
  generating:       { color: "bg-indigo-50 text-indigo-700 border-indigo-200",   label: "Generating…" },
  ready:            { color: "bg-emerald-50 text-emerald-700 border-emerald-200",label: "Ready" },
  failed:           { color: "bg-red-50 text-red-700 border-red-200",             label: "Failed" },
};

export default function CompetitorReportsPage() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const r = await axios.get(`${API}/competitor-reports`);
      setReports(r.data || []);
    } catch (e) {
      toast.error(errMsg(e, "Failed to load competitor reports"));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };
  useEffect(() => { load(); }, []);

  // Any in-flight rows (discovering/generating) → auto-poll so the list
  // reflects completion without the rep hitting refresh.
  useEffect(() => {
    const inflight = reports.some((r) => ["discovering", "generating"].includes(r.status));
    if (!inflight) return;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [reports]);

  const del = async (id) => {
    if (!window.confirm("Delete this report?")) return;
    try {
      await axios.delete(`${API}/competitor-reports/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(errMsg(e, "Delete failed"));
    }
  };

  return (
    <div className="space-y-6" data-testid="competitor-reports-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-outfit text-3xl font-bold tracking-tight flex items-center gap-2">
            <ScanEye className="h-7 w-7 text-indigo-600" />
            Competitor Reports
          </h1>
          <p className="text-muted-foreground mt-1 max-w-2xl">
            Generate a decision-ready competitive analysis for any prospect. Claude 4.5 Sonnet
            researches the client + 3 competitors live — reputation, ads, positioning, SEO — and
            hands back a branded deck.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => { setRefreshing(true); load(); }}
            data-testid="refresh-competitor-reports"
          >
            <RefreshCw className={`h-4 w-4 mr-1 ${refreshing ? "animate-spin" : ""}`} /> Refresh
          </Button>
          <Button asChild data-testid="new-competitor-report-btn" className="bg-indigo-600 hover:bg-indigo-700">
            <Link to="/prospect-iq/competitor-reports/new">
              <Plus className="h-4 w-4 mr-2" /> New Competitor Report
            </Link>
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : reports.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="py-16 text-center">
            <Search className="h-12 w-12 mx-auto text-muted-foreground opacity-30 mb-3" />
            <p className="font-medium">No competitor reports yet</p>
            <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
              Kick off a report with just the prospect&apos;s business name — Claude will find the top
              3 competitors and build the deck for you.
            </p>
            <Button
              className="mt-4 bg-indigo-600 hover:bg-indigo-700"
              onClick={() => navigate("/prospect-iq/competitor-reports/new")}
              data-testid="empty-new-competitor-report-btn"
            >
              <Plus className="h-4 w-4 mr-2" /> Start your first report
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="border rounded-xl bg-white overflow-hidden" data-testid="competitor-reports-table">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="text-left px-4 py-3">Prospect</th>
                <th className="text-left px-4 py-3">Client</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Created by</th>
                <th className="text-left px-4 py-3">Created</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {reports.map((r) => {
                const s = STATUS_STYLE[r.status] || STATUS_STYLE.draft;
                const created = r.created_at ? new Date(r.created_at).toLocaleDateString() : "—";
                return (
                  <tr key={r.id} className="border-t hover:bg-zinc-50/60 transition"
                      data-testid={`competitor-row-${r.id}`}>
                    <td className="px-4 py-3">
                      <Link to={`/prospect-iq/competitor-reports/${r.id}`}
                            className="font-medium text-indigo-700 hover:underline">
                        {r.business_name}
                      </Link>
                      {r.location && (
                        <div className="text-xs text-muted-foreground mt-0.5">{r.location}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {r.client_name ? (
                        <span className="text-indigo-700">{r.client_name}</span>
                      ) : "—"}
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant="outline" className={s.color}>
                        {["discovering", "generating"].includes(r.status) && (
                          <Loader2 className="h-3 w-3 mr-1 animate-spin inline" />
                        )}
                        {s.label}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{r.created_by_name || "—"}</td>
                    <td className="px-4 py-3 text-muted-foreground">{created}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-1">
                        <Button asChild variant="ghost" size="sm" data-testid={`view-competitor-${r.id}`}>
                          <Link to={`/prospect-iq/competitor-reports/${r.id}`}>
                            Open <ArrowRight className="h-3.5 w-3.5 ml-1" />
                          </Link>
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => del(r.id)}
                                data-testid={`delete-competitor-${r.id}`}>
                          <Trash2 className="h-3.5 w-3.5 text-red-500" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
